<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Otso Panalo Game</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Game Details</h2>

    <v-row>
      <v-col cols="12">
        <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <v-card :loading="loading" class="shadow">
          <v-card-text class="pb-0">
            <div class="text-center title">
              {{ details.league || "Otso Panalo" }}

              <v-btn
                v-if="canEditGame"
                small
                color="primary"
                @click="dialog = true"
                absolute
                right
                class="pe-2 d-none d-sm-inline-block"
              >
                <v-icon left>mdi-pencil</v-icon>
                Edit
              </v-btn>

              <v-btn
                v-if="canEditGame"
                icon
                color="primary"
                @click="dialog = true"
                absolute
                right
                class="pe-2 d-inline-block d-sm-none"
              >
                <v-icon size="large">mdi-pencil</v-icon>
              </v-btn>

              <div class="d-flex justify-end mt-3" style="gap: 8px">
                <v-btn
                  v-if="canCancelGame"
                  small
                  outlined
                  color="error"
                  :loading="cancelLoading"
                  :disabled="loading || cancelLoading"
                  @click="openCancelDialog"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel Game
                </v-btn>
              </div>
            </div>
          </v-card-text>

          <v-card-title>
            <v-row justify="center" align="center">
              <v-col cols="auto" class="text-center" align-self="center">
                <v-avatar
                  size="100px"
                  tile
                  color="blue lighten-5"
                  class="primary--text text-h5 font-weight-bold"
                >
                  <v-img v-if="details.team_a_logo" :src="details.team_a_logo">
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="black"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <span v-else>{{ teamAInitials }}</span>
                </v-avatar>

                <div>
                  <span class="text-caption">
                    {{ details.team_a_name }} {{ details.team_a_alias }}
                  </span>
                </div>
              </v-col>

              <v-col cols="auto" class="text-center">
                <span class="text-overline my-2 mx-2 font-italic">vs</span>
              </v-col>

              <v-col cols="auto" class="text-center">
                <v-avatar
                  size="100px"
                  tile
                  color="deep-orange lighten-5"
                  class="deep-orange--text text--darken-2 text-h5 font-weight-bold"
                >
                  <v-img v-if="details.team_b_logo" :src="details.team_b_logo">
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="black"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <span v-else>{{ teamBInitials }}</span>
                </v-avatar>

                <div>
                  <span class="text-caption">
                    {{ details.team_b_name }} {{ details.team_b_alias }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <p class="mb-1 overline primary--text">Game Details</p>

                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody class="table-borderless">
                      <tr>
                        <td style="width: 36%" class="grey--text">Game ID</td>
                        <td>
                          <span>#{{ details.id || "-" }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Schedule</td>
                        <td>
                          <span>{{ formattedSchedule }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Betting Deadline</td>
                        <td>
                          <span>{{ formattedBettingDeadline }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Status</td>
                        <td>
                          <v-chip small :class="statusChipClass">
                            {{ statusLabel }}
                          </v-chip>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Betting</td>
                        <td>
                          <div class="d-flex flex-wrap align-center">
                            <v-chip
                              small
                              :class="bettingChipClass"
                              class="mr-3"
                            >
                              {{ bettingLabel }}
                            </v-chip>

                            <div
                              v-if="canToggleBetting"
                              class="d-inline-flex align-center"
                            >
                              <span class="grey--text text-caption mr-2">
                                {{ bettingEnabled ? "Disable" : "Enable" }}
                              </span>
                              <v-switch
                                class="my-0 py-0"
                                color="success"
                                inset
                                :input-value="bettingEnabled"
                                hide-details
                                :disabled="switchBettingDisabled"
                                @click.native.prevent.stop.capture="
                                  openBettingConfirmation
                                "
                              ></v-switch>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Ending Game ID</td>
                        <td>
                          <span>#{{ details.ending_game_id || "-" }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Gross Bets</td>
                        <td>{{ moneyFormat(details.gross_bets) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Total Winnings</td>
                        <td>{{ moneyFormat(details.total_winnings) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">GGR</td>
                        <td class="font-weight-bold">
                          {{ moneyFormat(grossGamingRevenue) }}
                        </td>
                      </tr>
                      <tr v-if="isCancelled">
                        <td class="grey--text">Cancellation Reason</td>
                        <td>
                          <span>{{ cancellationReasonLabel }}</span>
                        </td>
                      </tr>
                      <tr v-if="isCancelled">
                        <td class="grey--text">Cancelled At</td>
                        <td>
                          <span>{{ formattedCancelledAt }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>

              <v-col cols="12" md="4">
                <p class="mb-1 mt-0 md-mt-3 overline primary--text">
                  Prize Configuration
                </p>

                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody class="table-borderless">
                      <tr>
                        <td style="width: 40%" class="grey--text">
                          Bet Amount
                        </td>
                        <td>{{ moneyFormat(details.bet_amount) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Jackpot Prize</td>
                        <td>{{ moneyFormat(details.jackpot_prize) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Second Prize</td>
                        <td>{{ moneyFormat(details.second_prize) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Third Prize</td>
                        <td>{{ moneyFormat(details.third_prize) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Fourth Prize</td>
                        <td>{{ moneyFormat(details.fourth_prize) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>

              <!-- <v-col cols="12" md="4">
                <p class="mb-1 mt-0 md-mt-3 overline primary--text">
                  Progressive Jackpot History
                </p>

                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody class="table-borderless">
                      <tr>
                        <td style="width: 40%" class="grey--text">Reason</td>
                        <td>{{ progressiveReasonLabel }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Base Jackpot</td>
                        <td>
                          {{ moneyFormat(details.default_jackpot_prize) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Source Game</td>
                        <td>
                          {{
                            details.progressive_source_game_id
                              ? `#${details.progressive_source_game_id}`
                              : "-"
                          }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Previous Jackpot</td>
                        <td>
                          {{
                            displayNullableMoney(details.previous_jackpot_prize)
                          }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Total Bets Used</td>
                        <td>
                          {{ moneyFormat(details.total_bet_amount_snapshot) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">2nd-4th Winnings</td>
                        <td>
                          {{ moneyFormat(details.total_secondary_winnings) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Remaining Pool</td>
                        <td>
                          {{ moneyFormat(details.remaining_pool_amount) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Rollover Added</td>
                        <td>{{ moneyFormat(details.rollover_amount) }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text">Jackpot Winners</td>
                        <td>{{ Number(details.jackpot_winner_count || 0) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col> -->

              <v-col cols="12" md="4" v-if="canGeneratePdf">
                <p class="mb-1 mt-0 md-mt-3 overline primary--text">
                  PDF Generation
                </p>

                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody class="table-borderless">
                      <tr>
                        <td style="width: 40%" class="grey--text">Action</td>
                        <td>
                          <v-btn
                            outlined
                            color="primary"
                            small
                            :loading="pdfLoading"
                            :disabled="loading || pdfLoading || !details.id"
                            @click="generatePdf"
                          >
                            <v-icon left>mdi-file-pdf-box</v-icon>
                            Generate Bets PDF
                          </v-btn>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text">Coverage</td>
                        <td>All bets for this Otso Panalo game</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="isCancelled"
          type="warning"
          dense
          outlined
          class="mt-4 mb-0"
        >
          This Otso Panalo game was cancelled due to
          {{ cancellationReasonLabel.toLowerCase() }}. All wagers for this game
          were refunded.
        </v-alert>

        <CreateResult
          v-if="canCreateResult && details.id && !isCancelled"
          :game-id="details.id"
          :game-details="details"
          :game-status="statusLabel"
          :disabled="loading"
          @created="handleResultCreated"
          @updated="handleResultUpdated"
          @approved="handleResultApproved"
        />

        <WinnersTable
          v-if="canViewWinners && details.id && !isCancelled"
          :game-id="details.id"
          :refresh-key="resultRefreshKey"
          :disabled="loading"
          @generated="handleWinnersGenerated"
        />
      </v-col>
    </v-row>

    <EditGame
      v-if="dialog"
      :id="id"
      @close="dialog = false"
      @updated="handleUpdated"
    />

    <v-dialog v-model="confirmBettingDialog" max-width="380">
      <v-card class="py-2">
        <v-card-title class="text-h6 d-block text-center">
          Confirm Betting Status
        </v-card-title>

        <v-card-text class="text-center">
          {{ bettingConfirmationText }}
        </v-card-text>

        <v-card-actions class="d-block text-center">
          <v-btn
            color="primary"
            outlined
            small
            :loading="switchStatusLoading"
            @click="submitBettingStatusChange"
          >
            Yes
          </v-btn>

          <v-btn
            color="black"
            outlined
            small
            :disabled="switchStatusLoading"
            @click="confirmBettingDialog = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="460">
      <v-card class="py-2">
        <v-card-title class="text-h6 d-block text-center">
          Confirm Cancellation
        </v-card-title>

        <v-card-text>
          <div class="text-center mb-4">
            Select the reason for voiding this Otso Panalo game. All wagers will
            be refunded.
          </div>

          <v-select
            v-model="cancelReason"
            :items="cancellationReasonOptions"
            item-text="text"
            item-value="value"
            label="Cancellation Reason"
            outlined
            dense
            hide-details="auto"
            :disabled="cancelLoading"
          ></v-select>
        </v-card-text>

        <v-card-actions class="d-block text-center">
          <v-btn
            color="error"
            outlined
            small
            :loading="cancelLoading"
            :disabled="!cancelReason"
            @click="submitCancellation"
          >
            Confirm
          </v-btn>

          <v-btn
            color="black"
            outlined
            small
            :disabled="cancelLoading"
            @click="cancelDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar ref="snackbar" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import formatDate from "@/helpers/formatDate";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";
import CreateResult from "@/components/otso_panalo/CreateResult";
import WinnersTable from "@/components/otso_panalo/WinnersTable";
import Snackbar from "@/components/Snackbar";
import EditGame from "@/views/otso_panalo/EditGame";

export default {
  components: {
    CreateResult,
    EditGame,
    Snackbar,
    WinnersTable,
  },

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data: () => ({
    loading: false,
    pdfLoading: false,
    switchStatusLoading: false,
    cancelLoading: false,
    dialog: false,
    confirmBettingDialog: false,
    cancelDialog: false,
    errorMessage: "",
    cancelReason: "",
    details: {},
    resultRefreshKey: 0,
    cancellationReasonOptions: [
      {
        text: "Cancelled",
        value: "cancelled",
      },
      {
        text: "Postponed beyond 48 hours",
        value: "postponed_beyond_48_hours",
      },
      {
        text: "Official results cannot be determined",
        value: "official_results_cannot_be_determined",
      },
      {
        text: "Game already started before close of betting",
        value: "started_before_close_of_betting",
      },
    ],
  }),

  computed: {
    ...mapState(["accountInfo"]),

    canEdit() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(32)
      );
    },

    canEditGame() {
      return this.canEdit && this.details.id && !this.isLockedForEdit;
    },

    canCreateResult() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(11)
      );
    },

    canGeneratePdf() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(33)
      );
    },

    canViewWinners() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(107)
      );
    },

    canToggleBetting() {
      return !!(
        this.accountInfo.access &&
        this.accountInfo.access.includes(32) &&
        this.details.id
      );
    },

    canCancelGame() {
      return !!(
        this.canEdit &&
        this.details.id &&
        !this.isFinished &&
        !this.isCancelled
      );
    },

    isLockedForEdit() {
      return (
        this.bettingEnabled || Number(this.details.game_status_id || 0) !== 1
      );
    },

    isFinished() {
      return Number(this.details.game_status_id || 0) === 3;
    },

    isCancelled() {
      return (
        Number(this.details.game_status_id || 0) === 4 ||
        this.statusLabel === "Cancelled"
      );
    },

    statusLabel() {
      return this.details.game_status || "Not yet started";
    },

    bettingLabel() {
      return this.details.betting || "Disabled";
    },

    bettingEnabled() {
      return this.bettingLabel === "Enabled";
    },

    bettingChipClass() {
      return this.bettingLabel === "Enabled"
        ? "green lighten-5 green--text"
        : "orange lighten-5 orange--text";
    },

    statusChipClass() {
      if (this.isCancelled) {
        return "red lighten-5 red--text";
      }

      if (this.isFinished) {
        return "green lighten-5 green--text";
      }

      return "orange lighten-5 orange--text";
    },

    switchBettingDisabled() {
      return (
        this.loading ||
        this.switchStatusLoading ||
        !this.details.id ||
        this.isFinished ||
        this.isCancelled
      );
    },

    bettingConfirmationText() {
      return this.bettingEnabled
        ? "Do you really want to disable betting for this Otso Panalo game?"
        : "Do you really want to enable betting for this Otso Panalo game?";
    },

    grossGamingRevenue() {
      const grossBets = Number(this.details.gross_bets || 0);
      const totalWinnings = Number(this.details.total_winnings || 0);

      return grossBets - totalWinnings;
    },

    prizeReleasedLabel() {
      return Number(this.details.prize_released) ? "Released" : "Pending";
    },

    prizeReleasedClass() {
      return Number(this.details.prize_released)
        ? "green lighten-5 green--text"
        : "orange lighten-5 orange--text";
    },

    formattedSchedule() {
      return this.details.schedule
        ? formatDate(this.details.schedule, true)
        : "-";
    },

    formattedBettingDeadline() {
      return this.details.betting_deadline
        ? formatDate(this.details.betting_deadline, true)
        : "-";
    },

    formattedCreatedAt() {
      return this.details.created_at
        ? formatDate(this.details.created_at, true)
        : "-";
    },

    formattedUpdatedAt() {
      return this.details.updated_at
        ? formatDate(this.details.updated_at, true)
        : "-";
    },

    formattedEnabledAt() {
      return this.details.enabled_betting_at
        ? formatDate(this.details.enabled_betting_at, true)
        : "-";
    },

    formattedDisabledAt() {
      return this.details.disabled_betting_at
        ? formatDate(this.details.disabled_betting_at, true)
        : "-";
    },

    formattedCancelledAt() {
      return this.details.cancelled_at
        ? formatDate(this.details.cancelled_at, true)
        : "-";
    },

    progressiveReasonLabel() {
      switch (this.details.progressive_reason) {
        case "pending_previous_game":
          return "Previous game not yet approved";
        case "rollover_added":
          return "Rollover added from previous game";
        case "no_remaining_pool":
          return "No remaining pool to add";
        case "jackpot_won_reset":
          return "Previous jackpot won and reset";
        case "cancelled_previous_game":
          return "Cancelled previous game carried forward";
        case "default_config":
          return "Started from default config";
        default:
          return "-";
      }
    },

    cancellationReasonLabel() {
      return this.getCancellationReasonLabel(this.details.cancellation_reason);
    },

    teamAInitials() {
      return this.getInitials(
        this.details.team_a_alias || this.details.team_a_name,
      );
    },

    teamBInitials() {
      return this.getInitials(
        this.details.team_b_alias || this.details.team_b_name,
      );
    },
  },

  created() {
    this.loadDetails();
  },

  watch: {
    id() {
      this.loadDetails();
    },
  },

  methods: {
    async loadDetails() {
      try {
        this.loading = true;
        this.errorMessage = "";

        const response = await responseGet(
          "",
          `/otso_panalo/games/details/${this.id}`,
        );

        if (response.status === 200) {
          this.details = response.data.details || {};
          this.cancelReason = this.details.cancellation_reason || "";
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else if (response.status === 404) {
          this.errorMessage = "Otso Panalo game not found.";
        } else {
          this.errorMessage = "Failed to load Otso Panalo game details.";
        }
      } catch (error) {
        this.errorMessage = "Failed to load Otso Panalo game details.";
      } finally {
        this.loading = false;
      }
    },

    moneyFormat(value) {
      const amount = Number(value || 0);

      return `PHP ${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    displayNullableMoney(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : this.moneyFormat(value);
    },

    getInitials(value) {
      if (!value) {
        return "--";
      }

      return String(value)
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },

    getMessage(payload, fallback) {
      if (!payload) {
        return fallback;
      }

      if (payload.messages) {
        const firstKey = Object.keys(payload.messages)[0];

        if (firstKey && typeof payload.messages[firstKey] === "string") {
          return payload.messages[firstKey];
        }
      }

      if (typeof payload.message === "string") {
        return payload.message;
      }

      return fallback;
    },

    getCancellationReasonLabel(reason) {
      switch (reason) {
        case "cancelled":
          return "Cancelled";
        case "postponed_beyond_48_hours":
          return "Postponed beyond 48 hours";
        case "official_results_cannot_be_determined":
          return "Official results cannot be determined";
        case "started_before_close_of_betting":
          return "Game already started before close of betting";
        default:
          return "-";
      }
    },

    openBettingConfirmation() {
      if (this.switchBettingDisabled) {
        return;
      }

      this.confirmBettingDialog = true;
    },

    async submitBettingStatusChange() {
      if (this.switchBettingDisabled || this.switchStatusLoading) {
        return;
      }

      try {
        this.switchStatusLoading = true;

        const formData = new FormData();
        formData.append("id", this.details.id);
        formData.append(
          "betting",
          this.bettingEnabled ? "Disabled" : "Enabled",
        );

        const response = await responsePost(
          formData,
          "otso_panalo/games/switch_status",
        );

        if (response.status === 200) {
          this.confirmBettingDialog = false;
          await this.loadDetails();
          this.$refs.snackbar.show(
            "success",
            "mdi-check-circle-outline",
            response.data.message ||
              "Otso Panalo betting status updated successfully.",
            4000,
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          throw new Error(
            this.getMessage(
              response.data,
              "Failed to update Otso Panalo betting status.",
            ),
          );
        }
      } catch (error) {
        this.$refs.snackbar.show(
          "error",
          "mdi-alert-circle-outline",
          error.message || "Failed to update Otso Panalo betting status.",
          4000,
        );
      } finally {
        this.switchStatusLoading = false;
      }
    },

    openCancelDialog() {
      if (!this.canCancelGame || this.cancelLoading) {
        return;
      }

      this.cancelDialog = true;
    },

    async submitCancellation() {
      if (!this.cancelReason || !this.canCancelGame || this.cancelLoading) {
        return;
      }

      try {
        this.cancelLoading = true;

        const formData = new FormData();
        formData.append("id", this.details.id);
        formData.append("cancellation_reason", this.cancelReason);

        const response = await responsePost(
          formData,
          "/otso_panalo/games/cancel",
        );

        if (response.status === 200) {
          this.cancelDialog = false;
          this.resultRefreshKey += 1;
          await this.loadDetails();
          this.$refs.snackbar.show(
            "success",
            "mdi-check-circle-outline",
            response.data.message || "Otso Panalo game cancelled successfully.",
            4000,
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          throw new Error(
            this.getMessage(
              response.data,
              "Failed to cancel Otso Panalo game.",
            ),
          );
        }
      } catch (error) {
        this.$refs.snackbar.show(
          "error",
          "mdi-alert-circle-outline",
          error.message || "Failed to cancel Otso Panalo game.",
          4000,
        );
      } finally {
        this.cancelLoading = false;
      }
    },

    getPdfFileName(headers) {
      const contentDisposition = headers.get("content-disposition") || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

      return fileNameMatch
        ? fileNameMatch[1]
        : `otso_panalo_bets_${this.id}.pdf`;
    },

    async generatePdf() {
      try {
        this.pdfLoading = true;

        const formData = new FormData();
        formData.append("game_id", this.id);

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/otso_panalo/bets/generate_pdf`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.$store.state.userToken}`,
            },
            body: formData,
          },
        );

        if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
          return;
        }

        if (!response.ok) {
          let message = "Failed to generate Otso Panalo bets PDF.";

          try {
            const errorData = await response.json();
            message = errorData.message || message;
          } catch (error) {
            message = "Failed to generate Otso Panalo bets PDF.";
          }

          throw new Error(message);
        }

        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = fileUrl;
        link.download = this.getPdfFileName(response.headers);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);

        this.$refs.snackbar.show(
          "success",
          "mdi-check-circle-outline",
          "Otso Panalo bets PDF generated successfully.",
          4000,
        );
      } catch (error) {
        this.$refs.snackbar.show(
          "error",
          "mdi-alert-circle-outline",
          error.message || "Failed to generate Otso Panalo bets PDF.",
          4000,
        );
      } finally {
        this.pdfLoading = false;
      }
    },

    async handleUpdated() {
      this.dialog = false;
      await this.loadDetails();
      this.$refs.snackbar.show(
        "success",
        "mdi-check-circle-outline",
        "Otso Panalo game updated successfully.",
        4000,
      );
    },

    async handleResultCreated() {
      await this.loadDetails();
      this.resultRefreshKey += 1;
      this.$refs.snackbar.show(
        "success",
        "mdi-check-circle-outline",
        "Otso Panalo result created successfully.",
        4000,
      );
    },

    async handleResultUpdated() {
      await this.loadDetails();
      this.resultRefreshKey += 1;
      this.$refs.snackbar.show(
        "success",
        "mdi-check-circle-outline",
        "Otso Panalo result updated successfully.",
        4000,
      );
    },

    async handleResultApproved() {
      await this.loadDetails();
      this.resultRefreshKey += 1;
      this.$refs.snackbar.show(
        "success",
        "mdi-check-circle-outline",
        "Otso Panalo result approved successfully.",
        4000,
      );
    },

    async handleWinnersGenerated(message) {
      await this.loadDetails();
      this.$refs.snackbar.show(
        "success",
        "mdi-check-circle-outline",
        message || "Otso Panalo winners generated successfully.",
        4000,
      );
    },

    goBack() {
      this.$router.push({ name: "OtsoPanaloGames" });
    },
  },
};
</script>
